import React, { FC } from "react";
import { Link } from "react-router-dom";

const SectionHeading: FC<{ children: React.ReactNode }> = ({ children }) => (
	<h2 className="text-2xl font-SatoshiBold text-headerblue mt-12 mb-4">
		{children}
	</h2>
);

const Paragraph: FC<{ children: React.ReactNode }> = ({ children }) => (
	<p className="font-SatoshiRegular text-[#475467] leading-relaxed mb-5">
		{children}
	</p>
);

const Bullets: FC<{ children: React.ReactNode }> = ({ children }) => (
	<ul className="list-disc pl-6 space-y-2 mb-6 font-SatoshiRegular text-[#475467] leading-relaxed">
		{children}
	</ul>
);

const Strong: FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="font-SatoshiBold text-headerblue">{children}</span>
);

const MailLink: FC<{ email: string }> = ({ email }) => (
	<a
		href={`mailto:${email}`}
		className="text-bblue font-SatoshiMedium hover:underline"
	>
		{email}
	</a>
);

const InternalLink: FC<{ to: string; children: React.ReactNode }> = ({
	to,
	children,
}) => (
	<Link to={to} className="text-bblue font-SatoshiMedium hover:underline">
		{children}
	</Link>
);

const TermsContent = () => {
	return (
		<section className="content-section bg-white px-6 pt-16 pb-16 lg:pb-24 lg:pt-20">
			<div className="max-w-4xl mx-auto">
				<div className="flex flex-col gap-1 mb-10 font-SatoshiMedium text-bash text-sm">
					<span>
						<Strong>Effective date:</Strong> 17 July 2026
					</span>
					<span>
						<Strong>Last updated:</Strong> 17 July 2026
					</span>
				</div>

				<Paragraph>
					These Terms of Use ("Terms") are a legal agreement between you and{" "}
					<Strong>Bankole Technologies</Strong> ("Bankolé", "we", "our", or
					"us") governing your access to and use of{" "}
					<a
						href="https://www.usebankole.com"
						target="_blank"
						rel="noreferrer"
						className="text-bblue font-SatoshiMedium hover:underline"
					>
						www.usebankole.com
					</a>
					, the Bankolé web platform, mobile applications, and any related
					products and services (collectively, the "Services").
				</Paragraph>
				<Paragraph>
					By creating an account, or by accessing or using the Services in any
					way, you agree to be bound by these Terms and by our{" "}
					<InternalLink to="/policy">Privacy Policy</InternalLink>. If you do
					not agree, do not use the Services.
				</Paragraph>

				<SectionHeading>1. Who can use the Services</SectionHeading>
				<Bullets>
					<li>
						You must be at least 18 years old, or the age of legal majority in
						your jurisdiction, and able to form a binding contract, to use the
						Services.
					</li>
					<li>
						If you use the Services on behalf of a company or other entity,
						you represent that you have authority to bind that entity, and
						"you" refers to both you and that entity.
					</li>
					<li>
						You are responsible for keeping your account credentials
						confidential and for all activity that occurs under your account.
					</li>
				</Bullets>

				<SectionHeading>2. The Services</SectionHeading>
				<Paragraph>
					Bankolé is a construction project-management platform that helps
					construction companies, contractors, consultants, developers, and
					project teams manage projects, tasks, budgets, documentation, site
					media, and team communication. We may add, change, or remove
					features at any time, and we may suspend or discontinue the
					Services (or any part of them), with reasonable notice where
					practicable.
				</Paragraph>

				<SectionHeading>3. Accounts and workspaces</SectionHeading>
				<Bullets>
					<li>
						You need an account to use most of the Services. You agree to
						provide accurate, current information and to keep it up to date.
					</li>
					<li>
						Workspaces and organisations let multiple users collaborate. If
						you create or administer a workspace, you are responsible for
						managing membership and permissions within it, and for ensuring
						members comply with these Terms.
					</li>
					<li>
						Content shared within a workspace or organisation (projects,
						tasks, budgets, messages, documents, and media) may remain
						accessible to that workspace or organisation even after an
						individual member's personal account is deleted, as described in
						our <InternalLink to="/policy">Privacy Policy</InternalLink>.
					</li>
				</Bullets>

				<SectionHeading>4. Your content</SectionHeading>
				<Bullets>
					<li>
						<Strong>"Your Content"</Strong> means the projects, tasks,
						budgets, documents, messages, photos, videos, and other materials
						you or your workspace create or upload within the Services.
					</li>
					<li>
						You retain ownership of Your Content. You grant Bankolé a
						worldwide, non-exclusive, royalty-free licence to host, store,
						reproduce, adapt, and display Your Content solely to operate,
						maintain, and improve the Services, and to provide the features
						you use (including the AI-assisted features described in Section
						6).
					</li>
					<li>
						You are solely responsible for Your Content and for having the
						rights and permissions necessary to upload and share it.
					</li>
				</Bullets>

				<SectionHeading>5. Acceptable use</SectionHeading>
				<Paragraph>You agree not to:</Paragraph>
				<Bullets>
					<li>
						use the Services for any unlawful purpose or in violation of any
						applicable law or regulation;
					</li>
					<li>
						upload content that infringes another party's intellectual
						property or privacy rights, or that is defamatory, fraudulent, or
						otherwise harmful;
					</li>
					<li>
						attempt to gain unauthorised access to the Services, other
						accounts, or our systems;
					</li>
					<li>
						interfere with or disrupt the integrity or performance of the
						Services, including by introducing malware or making excessive
						automated requests;
					</li>
					<li>
						reverse-engineer, decompile, or attempt to extract the source code
						of the Services, except where permitted by applicable law;
					</li>
					<li>
						misrepresent your identity or affiliation, or impersonate any
						person or entity; or
					</li>
					<li>
						use the Services to build a product or service that competes with
						the Services.
					</li>
				</Bullets>
				<Paragraph>
					We may investigate violations of this section and take appropriate
					action, including suspending or terminating the accounts involved.
				</Paragraph>

				<SectionHeading>6. AI-assisted features</SectionHeading>
				<Paragraph>
					The Services include optional AI-powered features that help
					summarise project activity, draft updates, and provide other
					productivity assistance. Outputs are generated automatically and
					may be inaccurate or incomplete — you are responsible for reviewing
					and verifying any AI-generated content before relying on it. Please
					do not submit information through AI features that you are not
					authorised to share.
				</Paragraph>

				<SectionHeading>7. Subscriptions, fees, and billing</SectionHeading>
				<Bullets>
					<li>
						Some features require a paid subscription (including Premium and
						Enterprise plans). Current pricing and plan features are described
						at <InternalLink to="/pricing">usebankole.com/pricing</InternalLink>{" "}
						and may change with notice.
					</li>
					<li>
						Subscriptions renew automatically for successive billing periods
						unless cancelled before the renewal date. You may cancel at any
						time; cancellation takes effect at the end of the current billing
						period.
					</li>
					<li>
						Fees are billed in advance and, except as required by law or as we
						otherwise state, are non-refundable.
					</li>
					<li>
						Payment processing is handled by third-party payment providers. We
						do not store your complete payment card information.
					</li>
				</Bullets>

				<SectionHeading>8. Third-party services</SectionHeading>
				<Paragraph>
					The Services may link to, integrate with, or rely on third-party
					services (for example, cloud hosting, analytics, and communications
					providers). We are not responsible for third-party services, and
					your use of them is governed by their own terms and policies.
				</Paragraph>

				<SectionHeading>9. Intellectual property</SectionHeading>
				<Paragraph>
					The Services — including their software, design, text, graphics,
					and trademarks (excluding Your Content) — are owned by Bankole
					Technologies or our licensors and are protected by intellectual
					property laws. Except for the limited licence to use the Services
					as intended, we grant you no rights in our intellectual property.
				</Paragraph>

				<SectionHeading>10. Suspension and termination</SectionHeading>
				<Bullets>
					<li>
						You may stop using the Services and delete your account at any
						time (
						<Strong>Account → Danger zone → Delete account</Strong> in the
						mobile app, or via{" "}
						<InternalLink to="/delete-account">
							usebankole.com/delete-account
						</InternalLink>
						).
					</li>
					<li>
						We may suspend or terminate your access to the Services if you
						violate these Terms, create risk or legal exposure for us, or as
						required by law, with notice where practicable.
					</li>
					<li>
						Sections that by their nature should survive termination
						(including Sections 4, 9, 11, 12, 13, and 14) will survive.
					</li>
				</Bullets>

				<SectionHeading>11. Disclaimers</SectionHeading>
				<Paragraph>
					The Services are provided <Strong>"as is"</Strong> and{" "}
					<Strong>"as available,"</Strong> without warranties of any kind,
					whether express, implied, or statutory, including implied
					warranties of merchantability, fitness for a particular purpose,
					and non-infringement. We do not warrant that the Services will be
					uninterrupted, error-free, or completely secure, or that
					AI-generated content will be accurate.
				</Paragraph>

				<SectionHeading>12. Limitation of liability</SectionHeading>
				<Paragraph>
					To the maximum extent permitted by law, Bankole Technologies will
					not be liable for any indirect, incidental, special, consequential,
					or punitive damages, or any loss of profits, revenue, data, or
					goodwill, arising from or related to your use of the Services. Our
					total liability for any claim arising out of or relating to these
					Terms or the Services will not exceed the amount you paid us for the
					Services in the 12 months before the claim arose, or $100 if you
					have not paid us anything.
				</Paragraph>

				<SectionHeading>13. Indemnification</SectionHeading>
				<Paragraph>
					You agree to indemnify and hold Bankole Technologies harmless from
					any claims, damages, liabilities, and expenses (including
					reasonable legal fees) arising from Your Content, your use of the
					Services, or your violation of these Terms or applicable law.
				</Paragraph>

				<SectionHeading>14. Governing law</SectionHeading>
				<Paragraph>
					These Terms are governed by the laws of the jurisdiction in which
					Bankole Technologies is established, without regard to
					conflict-of-law principles, unless applicable law requires
					otherwise.
				</Paragraph>

				<SectionHeading>15. Changes to these Terms</SectionHeading>
				<Paragraph>
					We may update these Terms from time to time. When we do, we will
					update the "Last updated" date above and, where changes are
					material, notify you through the website, email, or within the
					Services. Continued use of the Services after changes take effect
					constitutes acceptance of the updated Terms.
				</Paragraph>

				<SectionHeading>16. Contact us</SectionHeading>
				<Paragraph>
					<Strong>Bankole Technologies</Strong>
				</Paragraph>
				<Bullets>
					<li>
						Website:{" "}
						<a
							href="https://www.usebankole.com"
							target="_blank"
							rel="noreferrer"
							className="text-bblue font-SatoshiMedium hover:underline"
						>
							https://www.usebankole.com
						</a>
					</li>
					<li>
						Email: <MailLink email="support@usebankole.com" />
					</li>
					<li>
						Need more help? Visit our{" "}
						<InternalLink to="/support">Support page</InternalLink>.
					</li>
				</Bullets>
			</div>
		</section>
	);
};

export default TermsContent;
